<script setup lang="ts">
import { computed, ref } from "vue";
import { close_small } from "pn-design-assets/pn-assets/icons";
import { storeToRefs } from "pinia";
import { userStickers } from "@/stores/stickers";
import { useTagManagementStore } from "@/stores/tagManagement";
import type { ApiTagCategory } from "@/types/tagManagement";

const stickerStore = userStickers();
const { createCollectionReqestParams } = storeToRefs(stickerStore);
const tagManagementStore = useTagManagementStore();
const { themes } = storeToRefs(tagManagementStore);
const validated = ref(false);

const findCategoryById = (
  categories: ApiTagCategory[],
  categoryId: number,
): ApiTagCategory | undefined => {
  for (const category of categories) {
    if (category.categoryId === categoryId) {
      return category;
    }
    const foundInChildren = findCategoryById(category.children, categoryId);
    if (foundInChildren) {
      return foundInChildren;
    }
  }
  return undefined;
};

const getCategoryTagsByLanguage = (
  language: "english" | "swedish",
): string[] => {
  const tags: string[] = [];
  createCollectionReqestParams.value.categories.forEach((categoryId) => {
    themes.value.forEach((theme) => {
      const category = findCategoryById(theme.categories, categoryId);
      const languageTags =
        category?.tags.find((tag) => tag.language === language)?.tags ?? [];
      tags.push(...languageTags);
    });
  });
  return tags;
};

const englishCategoryTags = computed(() =>
  getCategoryTagsByLanguage("english"),
);
const swedishCategoryTags = computed(() =>
  getCategoryTagsByLanguage("swedish"),
);

const onTagEntered = (event: any, language: string) => {
  const tag: string = event.target.value;
  if (!tag.trim()) return;
  if (language === "swedish") {
    stickerStore.populateCreateCollectionRequest(
      [...createCollectionReqestParams.value.svTags, tag.trim()],
      "svTags",
    );
  } else if (language === "english") {
    stickerStore.populateCreateCollectionRequest(
      [...createCollectionReqestParams.value.enTags, tag.trim()],
      "enTags",
    );
  }
  event.target.value = "";
};

const removeTag = (tag: string, language: string) => {
  if (language === "swedish") {
    const filtered = createCollectionReqestParams.value.svTags.filter(
      (existingTag) => existingTag !== tag,
    );
    stickerStore.populateCreateCollectionRequest(filtered, "svTags");
  } else if (language === "english") {
    const filtered = createCollectionReqestParams.value.enTags.filter(
      (existingTag) => existingTag !== tag,
    );
    stickerStore.populateCreateCollectionRequest(filtered, "enTags");
  }
};

const getTagError = (language: string): string => {
  if (!validated.value) return "";
  if (
    language === "swedish" &&
    createCollectionReqestParams.value.svTags.length === 0
  ) {
    return "At least one Swedish tag is required";
  }
  if (
    language === "english" &&
    createCollectionReqestParams.value.enTags.length === 0
  ) {
    return "At least one English tag is required";
  }
  return "";
};

const validate = (): boolean => {
  validated.value = true;
  return (
    createCollectionReqestParams.value.svTags.length > 0 &&
    createCollectionReqestParams.value.enTags.length > 0
  );
};

defineExpose({ validate });
</script>

<template>
  <section>
    <div class="tags-step">
      <h3 class="pn-m">
        Option to add additonal tags to describe the collection
      </h3>
      <p class="pn-s width-67">
        Below you can see the tags already added to the collection (by your
        selections in the previous steps). Are there additional words you could
        use to describe the collection as a whole? If not, just leave this
        blank.
      </p>
      <div class="sticker-list">
        <div
          class="sticker-item"
          v-for="sticker in createCollectionReqestParams.stickers"
          :key="sticker.filename"
        >
          <img
            class="thumb"
            :src="`data:image/svg+xml;base64,${sticker.base64}`"
            :alt="sticker.filename"
          />
        </div>
      </div>
      <div class="tag-inputs">
        <div class="tag-column">
          <pn-input
            label="Swedish tag"
            helpertext="Enter one word at a time separated by blank space."
            :error="getTagError('swedish')"
            @keydown.space.prevent="onTagEntered($event, 'swedish')"
          ></pn-input>
          <div
            class="tag-list"
            v-if="
              createCollectionReqestParams.svTags.length > 0 ||
              swedishCategoryTags.length > 0
            "
          >
            <span class="pn-s tag-label">Tags:</span>
            <pn-tag
              small
              :label="tag"
              :icon="close_small"
              v-for="(tag, index) in createCollectionReqestParams.svTags"
              :key="`sv-tag-${index}`"
              @click="removeTag(tag, 'swedish')"
            ></pn-tag>
            <pn-tag
              small
              appearance="discrete"
              :label="tag"
              v-for="(tag, index) in swedishCategoryTags"
              :key="`sv-category-tag-${index}`"
            ></pn-tag>
          </div>
        </div>
        <div class="tag-column">
          <pn-input
            label="English tag"
            helpertext="Enter one word at a time separated by blank space."
            :error="getTagError('english')"
            @keydown.space.prevent="onTagEntered($event, 'english')"
          ></pn-input>
          <div
            class="tag-list"
            v-if="
              createCollectionReqestParams.enTags.length > 0 ||
              englishCategoryTags.length > 0
            "
          >
            <span class="pn-s tag-label">Tags:</span>
            <pn-tag
              small
              :label="tag"
              :icon="close_small"
              v-for="(tag, index) in createCollectionReqestParams.enTags"
              :key="`en-tag-${index}`"
              @click="removeTag(tag, 'english')"
            ></pn-tag>
            <pn-tag
              small
              appearance="discrete"
              :label="tag"
              v-for="(tag, index) in englishCategoryTags"
              :key="`en-category-tag-${index}`"
            ></pn-tag>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.tags-step {
  display: flex;
  flex-direction: column;
  padding-left: 32px;
}
.width-67 {
  width: 67%;
}
.sticker-list {
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  gap: 10px;
  padding: 8px 0;
}
.sticker-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  height: 60px;
}
.thumb {
  width: 100%;
  height: 60px;
  object-fit: contain;
}
.tag-inputs {
  display: flex;
  gap: 32px;
  padding-top: 15px;
}
.tag-column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  pn-input {
    width: 100%;
  }
}
.tag-list {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 8px;
}
.tag-label {
  font-weight: 600;
}
</style>
